import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRoute } from "../hooks/routes/useRoute";
import { useUpsertRoutes } from "../hooks/routes/useUpsertRoutes";
import { useRouteListen } from "../hooks/routes/useRouteListen";

const RouteContext = createContext(null);

export function RouteProvider({ children }) {
  const [routeId, setRouteId] = useState("");
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [listenEnabled, setListenEnabled] = useState(false);

  const singleFetch = useRoute(routeId, { enabled: fetchEnabled });
  const singleListen = useRouteListen(routeId, { enabled: listenEnabled });
  const upsertRoutes = useUpsertRoutes();

  const fetchRouteById = useCallback((routeId) => {
    setRouteId(routeId);
    setFetchEnabled(true);
    setListenEnabled(false);
  }, []);

  const listenRouteById = useCallback((routeId) => {
    setRouteId(routeId);
    setFetchEnabled(false);
    setListenEnabled(true);
  }, []);

  const unlistenRoute = useCallback(() => {
    setListenEnabled(false);
  }, []);

  const value = useMemo(
    () => ({
      singleFetch: {
        data: singleFetch.data,
        isSuccess: singleFetch.isSuccess,
        isPending: singleFetch.isPending,
        error: singleFetch.error,
        routeId,
        fetchRouteById,
      },
      singleListen: {
        data: singleListen.data,
        isSuccess: singleListen.isSuccess,
        isPending: singleListen.isPending,
        error: singleListen.error,
        routeId,
        listenRouteById,
        unlistenRoute,
      },
      upsertRoutes: {
        mutateAsync: upsertRoutes.mutateAsync,
      },
    }),
    [
      singleFetch.data,
      singleFetch.isSuccess,
      singleFetch.isPending,
      singleFetch.error,
      routeId,
      fetchRouteById,
      singleListen.data,
      singleListen.isSuccess,
      singleListen.isPending,
      singleListen.error,
      listenRouteById,
      unlistenRoute,
      upsertRoutes.mutateAsync,
    ],
  );

  return <RouteContext.Provider value={value}>{children}</RouteContext.Provider>;
}

export function useRouteContext() {
  const context = useContext(RouteContext);

  if (!context) {
    throw new Error("useRouteContext must be used inside RouteContext");
  }

  return context;
}
